import { Dialog, SelectCustomizable } from 'ui';
import PropTypes from 'prop-types';
import React from 'react';
import './_quiz-location-dialog.scss';
import { LOCATION_DIALOG_TARGETS } from './locationDialogTargets';
import useQuizLocationDialog from './useQuizLocationDialog';

/**
 * QuizLocationDialog - Dialog for selecting a location (Class or licensed program > Unit > Lesson)
 * and navigating to it: to create a quiz (target `quiz`), or to add a Tutor AI document as an HTML
 * page (target `mint`, only lessons with the power view).
 */
function QuizLocationDialog({ conversationId, isOpen, onClose, onNavigate, target }) {
  const {
    canNavigate,
    confirmText,
    courseOptions,
    dialogTitle,
    handleConfirm,
    handleCourseChange,
    handleLessonChange,
    handleTestGroupChange,
    handleUnitChange,
    hasContentError,
    hasLessons,
    hasMultipleTestSections,
    hasNoGroups,
    hasNoMintLessons,
    hasNoTestSection,
    isEdelvives,
    isLessonSelectDisabled,
    isLoadingTestSections,
    isLoadingUnits,
    isUnitSelectDisabled,
    lessonError,
    lessonOptions,
    selectedCourse,
    selectedLesson,
    selectedTestGroup,
    selectedUnit,
    testSections,
    unitError,
    unitOptions,
    t,
  } = useQuizLocationDialog({ isOpen, onClose, onNavigate, target, conversationId });

  return (
    <Dialog
      isOpen={isOpen}
      onClose={onClose}
      onCancel={onClose}
      onSuccess={handleConfirm}
      title={dialogTitle}
      buttonTextCancel={t('actions:Cancel')}
      buttonTextSuccess={confirmText}
      isDisabledSuccess={!canNavigate}
      size="l"
      className="quiz-location-dialog"
    >
      <div className="quiz-location-dialog__content">
        {/* Course/Program Selection */}
        <div className="quiz-location-dialog__field">
          <SelectCustomizable
            aria-label={t('chatbot:quiz_location_select_location')}
            options={courseOptions}
            value={selectedCourse}
            onChange={handleCourseChange}
            placeholder={t('chatbot:quiz_location_select_location')}
            isSearchable={true}
            isClearable={false}
          />
        </div>

        {/* Test Group Selection - Edelvives only, when multiple groups */}
        {hasMultipleTestSections && (
          <div className="quiz-location-dialog__field">
            <SelectCustomizable
              aria-label={t('chatbot:quiz_location_select_section')}
              options={testSections}
              value={selectedTestGroup}
              onChange={handleTestGroupChange}
              placeholder={t('chatbot:quiz_location_select_section')}
              isSearchable={true}
              isClearable={false}
              isLoading={isLoadingTestSections}
            />
          </div>
        )}

        {/* Error when no test section exists - Edelvives only */}
        {hasNoTestSection && (
          <div className="quiz-location-dialog__error">
            <div className="quiz-location-dialog__error-title">{t('chatbot:teacher_tools_error_no_test_section_title')}</div>
            <div className="quiz-location-dialog__error-message">{t('chatbot:teacher_tools_error_no_test_section_message')}</div>
          </div>
        )}

        {/* Error when test section exists but has no groups - Edelvives only */}
        {hasNoGroups && (
          <div className="quiz-location-dialog__error">
            <div className="quiz-location-dialog__error-title">{t('chatbot:teacher_tools_error_no_groups_title')}</div>
            <div className="quiz-location-dialog__error-message">{t('chatbot:teacher_tools_error_no_groups_message')}</div>
          </div>
        )}

        {/* Unit Selection - hide for Edelvives (uses toolbox flow) */}
        {!isEdelvives && (
          <div className="quiz-location-dialog__field">
            <SelectCustomizable
              aria-label={t('chatbot:quiz_location_select_unit')}
              options={unitOptions}
              value={selectedUnit}
              onChange={handleUnitChange}
              placeholder={t('chatbot:quiz_location_select_unit')}
              isSearchable={false}
              isClearable={false}
              isDisabled={isUnitSelectDisabled}
              isLoading={isLoadingUnits}
              error={unitError}
            />
          </div>
        )}

        {/* Lesson Selection - hide for Edelvives and modules-only programs */}
        {!isEdelvives && hasLessons && (
          <div className="quiz-location-dialog__field">
            <SelectCustomizable
              aria-label={t('chatbot:quiz_location_select_lesson')}
              options={lessonOptions}
              value={selectedLesson}
              onChange={handleLessonChange}
              placeholder={t('chatbot:quiz_location_select_lesson')}
              isSearchable={true}
              isClearable={false}
              isDisabled={isLessonSelectDisabled}
              error={lessonError}
            />
          </div>
        )}

        {/* No lesson of the course can take an HTML page - target mint only */}
        {hasNoMintLessons && (
          <div className="quiz-location-dialog__error" role="status">
            <div className="quiz-location-dialog__error-title">{t('chatbot:mint_location_no_lessons_title')}</div>
            <div className="quiz-location-dialog__error-message">{t('chatbot:mint_location_no_lessons_message')}</div>
          </div>
        )}

        {/* Error message - only for non-Edelvives tenants */}
        {hasContentError && (
          <div className="quiz-location-dialog__error">
            <div className="quiz-location-dialog__error-message">{t('chatbot:teacher_tools_error_content_unavailable_message')}</div>
          </div>
        )}
      </div>
    </Dialog>
  );
}

QuizLocationDialog.propTypes = {
  conversationId: PropTypes.string,
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onNavigate: PropTypes.func,
  target: PropTypes.oneOf(Object.values(LOCATION_DIALOG_TARGETS)),
};

QuizLocationDialog.defaultProps = {
  conversationId: null,
  onNavigate: undefined,
  target: LOCATION_DIALOG_TARGETS.QUIZ,
};

export default QuizLocationDialog;
